/**
 * LocalStorage cache for i18n draft overrides.
 *
 * Allows instant preview of text changes before they are published.
 * Cache expires after 15 minutes to avoid stale data.
 */

const CACHE_KEY = 'i18n-draft-overrides';
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

interface CacheEntry {
  namespace: string;
  key: string;
  locale: string;
  value: unknown;
  timestamp: number;
}

interface CacheData {
  entries: CacheEntry[];
  version: number;
}

function getCache(): CacheData {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return { entries: [], version: 0 };
    const data = JSON.parse(raw) as CacheData;
    // Filter out expired entries
    const now = Date.now();
    data.entries = data.entries.filter(e => now - e.timestamp < CACHE_TTL_MS);
    return data;
  } catch {
    return { entries: [], version: 0 };
  }
}

function saveCache(data: CacheData): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch {
    // Storage full or unavailable - fail silently
  }
}

/**
 * Add or update a draft override in the cache.
 */
export function cacheDraftOverride(
  namespace: string,
  key: string,
  locale: string,
  value: unknown
): void {
  const cache = getCache();

  // Remove existing entry for this key
  cache.entries = cache.entries.filter(
    e => !(e.namespace === namespace && e.key === key && e.locale === locale)
  );

  // Add new entry
  cache.entries.push({
    namespace,
    key,
    locale,
    value,
    timestamp: Date.now(),
  });

  cache.version++;
  saveCache(cache);
}

/**
 * Get all cached draft overrides for a locale.
 * Returns a nested object: { namespace: { key: value } }
 */
export function getCachedDraftOverrides(locale: string): Record<string, Record<string, unknown>> {
  const cache = getCache();
  const result: Record<string, Record<string, unknown>> = {};

  for (const entry of cache.entries) {
    if (entry.locale !== locale) continue;

    if (!result[entry.namespace]) {
      result[entry.namespace] = {};
    }

    // Support dot-notation keys (e.g., "navigation.characters")
    const parts = entry.key.split('.');
    let cursor: Record<string, unknown> = result[entry.namespace]!;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]!;
      const isLast = i === parts.length - 1;

      if (isLast) {
        cursor[part] = entry.value;
      } else {
        if (!cursor[part] || typeof cursor[part] !== 'object') {
          cursor[part] = {};
        }
        cursor = cursor[part] as Record<string, unknown>;
      }
    }
  }

  return result;
}

/**
 * Clear all cached draft overrides.
 * Call this after publishing or discarding changes.
 */
export function clearDraftOverridesCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Fail silently
  }
}

/**
 * Check if there are any cached draft overrides.
 */
export function hasCachedDraftOverrides(): boolean {
  const cache = getCache();
  return cache.entries.length > 0;
}

/**
 * Get the number of cached draft overrides.
 */
export function getCachedDraftCount(): number {
  const cache = getCache();
  return cache.entries.length;
}
